import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";

const STORAGE_KEY = "pcg_chat_session";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [startForm, setStartForm] = useState({ name: "", email: "" });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const lastTsRef = useRef(null);

  useEffect(() => {
    if (!session) return;
    let active = true;

    const fetchMsgs = async (initial = false) => {
      try {
        const params = lastTsRef.current && !initial ? { since: lastTsRef.current } : {};
        const { data } = await api.get(`/chat/messages/${session.session_id}`, { params });
        if (!active) return;
        if (initial) {
          setMessages(data);
          if (data.length) lastTsRef.current = data[data.length - 1].created_at;
        } else if (data.length) {
          setMessages((prev) => [...prev, ...data]);
          lastTsRef.current = data[data.length - 1].created_at;
        }
      } catch (_) {}
    };
    fetchMsgs(true);
    const interval = setInterval(() => fetchMsgs(false), 3000);
    return () => { active = false; clearInterval(interval); };
  }, [session]);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  const startChat = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/chat/start", startForm);
      const s = { session_id: data.session_id, name: startForm.name || "Guest", email: startForm.email };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
      setSession(s);
    } catch (err) {
      // Even on error, user can try again
      console.error(formatApiError(err));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !session) return;
    setSending(true);
    const payload = {
      session_id: session.session_id,
      sender: "client",
      sender_name: session.name,
      text: text.trim(),
    };
    try {
      const { data } = await api.post("/chat/messages", payload);
      setMessages((prev) => [...prev, data]);
      lastTsRef.current = data.created_at;
      setText("");
    } catch (err) {
      console.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-24 right-5 z-[60]">
      {open && (
        <div
          data-testid={TID.chatWindow}
          className="mb-3 w-[340px] sm:w-[380px] h-[500px] max-h-[calc(100vh-180px)] card-surface rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        >
          <div className="bg-black/60 px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
                <Sparkles size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-serif-display text-white text-base leading-tight">Premier Concierge</div>
                <div className="text-[11px] text-white/50">Avg reply &lt; 1 hour</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white" aria-label="Close chat">
              <X size={18} />
            </button>
          </div>

          {!session ? (
            <form onSubmit={startChat} className="p-6 flex-grow flex flex-col">
              <div className="text-sm text-white/70 mb-6">Welcome! Share your name to start chatting with our team.</div>
              <label className="overline text-white/50 block mb-2">Name</label>
              <input
                className="input-line"
                value={startForm.name}
                onChange={(e) => setStartForm({...startForm, name: e.target.value})}
                placeholder="Your name"
                data-testid={TID.chatStartName}
                required
              />
              <label className="overline text-white/50 block mb-2 mt-5">Email (optional)</label>
              <input
                type="email"
                className="input-line"
                value={startForm.email}
                onChange={(e) => setStartForm({...startForm, email: e.target.value})}
                placeholder="you@example.com"
                data-testid={TID.chatStartEmail}
              />
              <button type="submit" className="btn-gold mt-8 justify-center" data-testid={TID.chatStartBtn}>
                Start Chat
              </button>
            </form>
          ) : (
            <>
              <div data-testid={TID.chatMessages} className="flex-grow overflow-y-auto p-5 space-y-3 bg-[#080808]">
                {messages.length === 0 && (
                  <div className="text-center text-white/40 text-sm py-8">
                    Send us a message — we typically reply within an hour.
                  </div>
                )}
                {messages.map((m) => (
                  <div
                    key={m.id || m.created_at}
                    className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                        m.sender === "client"
                          ? "bg-[#D4AF37] text-black rounded-br-sm"
                          : "bg-[#1a1a1a] text-white border border-white/5 rounded-bl-sm"
                      }`}
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={sendMessage} className="p-3 border-t border-white/5 bg-black/40 flex items-center gap-2">
                <input
                  className="flex-grow bg-transparent text-white text-sm outline-none px-3 py-2"
                  placeholder="Type your message..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  data-testid={TID.chatInput}
                />
                <button
                  type="submit"
                  disabled={!text.trim() || sending}
                  data-testid={TID.chatSend}
                  className="h-10 w-10 rounded-full bg-[#D4AF37] text-black flex items-center justify-center disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </form>
            </>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        data-testid={TID.chatToggle}
        className="h-14 w-14 rounded-full btn-gold pulse-gold !p-0 flex items-center justify-center"
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
};
